"use strict";

var _path = _interopRequireDefault(require("path"));

var _constants = require("./constants");

var _utils = require("./utils");

var _uploadError = _interopRequireDefault(require("./upload-error"));

var _errorCodes = _interopRequireDefault(require("./error-codes"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

/**
 * Retrieves the option indicating whether or not the upload is deep. Takes
 * into account that the options might not be FileSystemUploadOptions.
 * @param {FileSystemUploadOptions|DirectBinaryUploadOptions} uploadOptions Options
 *  to retrieve value from.
 * @returns {boolean} True if it's a deep upload, false otherwise.
 */
function isDeepUpload(uploadOptions) {
  if (!uploadOptions.getDeepUpload) {
    // default to false if the class received an options instance
    // not of type FileSystemUploadOptions.
    return false;
  }

  return uploadOptions.getDeepUpload();
}
/**
 * Separates a list of files into a lookup based on its target remote path.
 *
 * @param {Array} files List of simple objects representing files. Is
 *  expected to at least have a "remoteUrl" element, which is expected
 *  to be normalized and not end with a forward slash. For example:
 *  http://adobe.com/myfile.jpg is valid, whereas http://adobe.com/myfile.jpg/
 *  is not valid.
 * @returns {object} Simple object whose keys are directory paths; values
 *  are an Array of objects as-is from the files parameter.
 */


function aggregateByRemoteDirectory(files) {
  var directoryAggregate = {};
  files.forEach(function (file) {
    var remoteDirectory = file.getParentRemoteUrl();

    if (!directoryAggregate[remoteDirectory]) {
      directoryAggregate[remoteDirectory] = new Set();
    }

    directoryAggregate[remoteDirectory].add(file);
  });
  return directoryAggregate;
}
/**
 * Retrieves the option specifying the maximum number of files that can be
 * uploaded at once. Takes into account that the options might not be
 * FileSystemUploadOptions.
 * @param {FileSystemUploadOptions|DirectBinaryUploadOptions} uploadOptions Options
 *  to retrieve value from.
 * @returns {number} Maximum number of files to upload.
 */


function getMaxFileCount(uploadOptions) {
  if (!uploadOptions.getMaxUploadFiles) {
    return _constants.DefaultValues.MAX_FILE_UPLOAD;
  }

  return uploadOptions.getMaxUploadFiles();
}
/**
 * Uses a processor function to clean a node name, then cleans generally disallowed characters
 * from the name.
 * @param {FileSystemUploadOptions} uploadOptions Used to retrieve the value to use when replacing
 *  invalid characters.
 * @param {function} processorFunction Will be given the provided node name as a single argument.
 *  Expected to return a Promise that will be resolved with the clean node name value.
 * @param {string} nodeName Value to be cleaned of invalid characters.
 * @returns {Promise} Will be resolved with the cleaned node name.
 */


function cleanNodeName(_x, _x2, _x3) {
  return _cleanNodeName.apply(this, arguments);
}
/**
 * Uses the given options to clean a folder name, then cleans generally disallowed characters
 * from the name.
 * @param {FileSystemUploadOptions} uploadOptions Used to retrieve the value to use when replacing
 *  invalid characters, and the function to call to clean the folder name.
 * @param {string} folderName Value to be cleaned of invalid characters.
 * @returns {Promise} Will be resolved with the clean name.
 */


function _cleanNodeName() {
  _cleanNodeName = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(uploadOptions, processorFunction, nodeName) {
    var processedName;
    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return processorFunction(nodeName);

          case 2:
            processedName = _context.sent;
            return _context.abrupt("return", processedName.replace(_constants.RegularExpressions.INVALID_CHARACTERS_REGEX, uploadOptions.getInvalidCharacterReplaceValue()));

          case 4:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _cleanNodeName.apply(this, arguments);
}

function cleanFolderName(_x4, _x5) {
  return _cleanFolderName.apply(this, arguments);
}
/**
 * Uses the given options to clean an asset name, then cleans generally disallowed characters
 * from the name.
 * @param {FileSystemUploadOptions} uploadOptions Used to retrieve the value to use when replacing
 *  invalid characters, and the function to call to clean the asset name.
 * @param {string} folderName Value to be cleaned of invalid characters.
 * @returns {Promise} Will be resolved with the clean name.
 */


function _cleanFolderName() {
  _cleanFolderName = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(uploadOptions, folderName) {
    return regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            return _context2.abrupt("return", cleanNodeName(uploadOptions, uploadOptions.getFolderNodeNameProcessor(), folderName));

          case 1:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _cleanFolderName.apply(this, arguments);
}

function cleanAssetName(_x6, _x7) {
  return _cleanAssetName.apply(this, arguments);
}

function _cleanAssetName() {
  _cleanAssetName = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee3(uploadOptions, assetName) {
    var _Path$parse, assetNameOnly, ext, cleanName;

    return regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _Path$parse = _path.default.parse(assetName), assetNameOnly = _Path$parse.name, ext = _Path$parse.ext;
            _context3.next = 3;
            return cleanNodeName(uploadOptions, uploadOptions.getAssetNodeNameProcessor(), assetNameOnly);

          case 3:
            cleanName = _context3.sent;
            return _context3.abrupt("return", "".concat(cleanName).concat(ext));

          case 5:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));
  return _cleanAssetName.apply(this, arguments);
}

function getItemManagerParent(_x8, _x9, _x10) {
  return _getItemManagerParent.apply(this, arguments);
}

function _getItemManagerParent() {
  _getItemManagerParent = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee4(itemManager, rootPath, localPath) {
    var normalizedPath, parent;
    return regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            normalizedPath = (0, _utils.normalizePath)(localPath);

            if (!(normalizedPath !== rootPath && !String(normalizedPath).startsWith("".concat(rootPath, "/")))) {
              _context4.next = 3;
              break;
            }

            throw new _uploadError.default('directory to upload is outside expected root', _errorCodes.default.INVALID_OPTIONS);

          case 3:
            if (!(normalizedPath !== rootPath)) {
              _context4.next = 7;
              break;
            }

            _context4.next = 6;
            return itemManager.getDirectory(normalizedPath.substr(0, normalizedPath.lastIndexOf('/')));

          case 6:
            parent = _context4.sent;

          case 7:
            return _context4.abrupt("return", parent);

          case 8:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));
  return _getItemManagerParent.apply(this, arguments);
}

module.exports = {
  aggregateByRemoteDirectory: aggregateByRemoteDirectory,
  isDeepUpload: isDeepUpload,
  getMaxFileCount: getMaxFileCount,
  cleanFolderName: cleanFolderName,
  cleanAssetName: cleanAssetName,
  getItemManagerParent: getItemManagerParent
};